import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../Button";
import type { FrameCropRect } from "./frameCropUtils";
import { getDefaultCropRect } from "./frameCropUtils";
import styles from "./FrameCropCanvas.module.css";
import { useTranslation } from "react-i18next";

type DragHandle = "move" | "nw" | "ne" | "sw" | "se" | null;

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  initialBox: FrameCropRect;
  dragHandle: DragHandle;
}

const MIN_SIZE = 4;
const HANDLE_HIT = 20;
const HANDLE_DRAW = 8;
/** 裁剪区最大展示高度（与 TFT 预览区一致，避免画布按像素撑开出现滚动条） */
const MAX_DISPLAY_HEIGHT_PX = 347;

export interface FrameCropCanvasProps {
  imageData: ImageData;
  /** 通常为设备 screenWidth / screenHeight */
  defaultCropWidth: number;
  defaultCropHeight: number;
  visible: boolean;
  onConfirm: (rect: FrameCropRect) => void;
  onCancel: () => void;
  /** 将选区重置为默认（居中、设备目标尺寸） */
  onResetSelection?: () => void;
}

const FrameCropCanvas: React.FC<FrameCropCanvasProps> = ({
  imageData,
  defaultCropWidth,
  defaultCropHeight,
  visible,
  onConfirm,
  onCancel,
  onResetSelection,
}) => {
  const { t } = useTranslation();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  /** 展示高度固定 367px，宽度按位图宽高比；若超过容器宽度则等比缩小（高度可能略小于 367） */
  const syncCanvasDisplaySize = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = canvasWrapRef.current;
    if (!canvas || !wrap) return;
    const iw = canvas.width;
    const ih = canvas.height;
    if (iw <= 0 || ih <= 0) return;

    const dh0 = MAX_DISPLAY_HEIGHT_PX;
    let dw = Math.round(iw * (dh0 / ih));
    const maxW = Math.max(1, wrap.clientWidth);
    if (dw > maxW) {
      dw = maxW;
    }
    const dh = Math.round(ih * (dw / iw));
    canvas.style.width = `${dw}px`;
    canvas.style.height = `${dh}px`;
  }, []);

  const [selectionBox, setSelectionBox] = useState<FrameCropRect>(() =>
    getDefaultCropRect(
      imageData.width,
      imageData.height,
      defaultCropWidth,
      defaultCropHeight
    )
  );

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialBox: selectionBox,
    dragHandle: null,
  });

  // 打开或换图时重置选区
  useEffect(() => {
    if (!visible) return;
    setSelectionBox(
      getDefaultCropRect(
        imageData.width,
        imageData.height,
        defaultCropWidth,
        defaultCropHeight
      )
    );
  }, [visible, imageData, defaultCropWidth, defaultCropHeight]);

  const clampBox = useCallback(
    (box: FrameCropRect): FrameCropRect => {
      const iw = imageData.width;
      const ih = imageData.height;
      let { x, y, width, height } = box;
      width = Math.max(MIN_SIZE, width);
      height = Math.max(MIN_SIZE, height);
      x = Math.max(0, Math.min(x, iw - width));
      y = Math.max(0, Math.min(y, ih - height));
      if (x + width > iw) width = iw - x;
      if (y + height > ih) height = ih - y;
      return { x, y, width, height };
    },
    [imageData.height, imageData.width]
  );

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const iw = imageData.width;
    const ih = imageData.height;
    canvas.width = iw;
    canvas.height = ih;

    ctx.putImageData(imageData, 0, 0);

    const box = selectionBox;

    // 选区外暗角
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    if (box.y > 0) {
      ctx.fillRect(0, 0, iw, box.y);
    }
    if (box.x > 0) {
      ctx.fillRect(0, box.y, box.x, box.height);
    }
    if (box.x + box.width < iw) {
      ctx.fillRect(
        box.x + box.width,
        box.y,
        iw - box.x - box.width,
        box.height
      );
    }
    if (box.y + box.height < ih) {
      ctx.fillRect(0, box.y + box.height, iw, ih - box.y - box.height);
    }

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.setLineDash([]);

    ctx.fillStyle = "#ffffff";
    const h = HANDLE_DRAW;
    ctx.fillRect(box.x - h / 2, box.y - h / 2, h, h);
    ctx.fillRect(box.x + box.width - h / 2, box.y - h / 2, h, h);
    ctx.fillRect(box.x - h / 2, box.y + box.height - h / 2, h, h);
    ctx.fillRect(box.x + box.width - h / 2, box.y + box.height - h / 2, h, h);

    ctx.fillStyle = "#fff";
    ctx.font = "bold 12px Arial";
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 4;
    ctx.fillText(
      `${Math.round(box.width)}×${Math.round(box.height)}`,
      box.x + 8,
      box.y - 8
    );
    ctx.shadowBlur = 0;

    requestAnimationFrame(() => {
      syncCanvasDisplaySize();
    });
  }, [imageData, selectionBox, syncCanvasDisplaySize]);

  useEffect(() => {
    if (!visible) return;
    drawCanvas();
  }, [visible, drawCanvas]);

  useEffect(() => {
    if (!visible) return;
    const el = canvasWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      syncCanvasDisplaySize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible, syncCanvasDisplaySize]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const canvasX = (e.clientX - rect.left) * scaleX;
      const canvasY = (e.clientY - rect.top) * scaleY;

      const box = selectionBox;
      let dragHandle: DragHandle = null;

      if (
        Math.abs(canvasX - box.x) < HANDLE_HIT &&
        Math.abs(canvasY - box.y) < HANDLE_HIT
      ) {
        dragHandle = "nw";
      } else if (
        Math.abs(canvasX - (box.x + box.width)) < HANDLE_HIT &&
        Math.abs(canvasY - box.y) < HANDLE_HIT
      ) {
        dragHandle = "ne";
      } else if (
        Math.abs(canvasX - box.x) < HANDLE_HIT &&
        Math.abs(canvasY - (box.y + box.height)) < HANDLE_HIT
      ) {
        dragHandle = "sw";
      } else if (
        Math.abs(canvasX - (box.x + box.width)) < HANDLE_HIT &&
        Math.abs(canvasY - (box.y + box.height)) < HANDLE_HIT
      ) {
        dragHandle = "se";
      } else if (
        canvasX >= box.x &&
        canvasX <= box.x + box.width &&
        canvasY >= box.y &&
        canvasY <= box.y + box.height
      ) {
        dragHandle = "move";
      }

      if (dragHandle) {
        setDragState({
          isDragging: true,
          startX: canvasX,
          startY: canvasY,
          initialBox: { ...box },
          dragHandle,
        });
        e.preventDefault();
      }
    },
    [selectionBox]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.isDragging || !dragState.dragHandle || !canvasRef.current)
        return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const currentX = (e.clientX - rect.left) * scaleX;
      const currentY = (e.clientY - rect.top) * scaleY;

      const deltaX = currentX - dragState.startX;
      const deltaY = currentY - dragState.startY;
      const ib = dragState.initialBox;
      const newBox: FrameCropRect = { ...ib };

      switch (dragState.dragHandle) {
        case "move":
          newBox.x = ib.x + deltaX;
          newBox.y = ib.y + deltaY;
          break;
        case "nw":
          newBox.x = ib.x + deltaX;
          newBox.y = ib.y + deltaY;
          newBox.width = ib.width - deltaX;
          newBox.height = ib.height - deltaY;
          break;
        case "ne":
          newBox.y = ib.y + deltaY;
          newBox.width = ib.width + deltaX;
          newBox.height = ib.height - deltaY;
          break;
        case "sw":
          newBox.x = ib.x + deltaX;
          newBox.width = ib.width - deltaX;
          newBox.height = ib.height + deltaY;
          break;
        case "se":
          newBox.width = ib.width + deltaX;
          newBox.height = ib.height + deltaY;
          break;
        default:
          break;
      }

      setSelectionBox(clampBox(newBox));
    },
    [dragState, clampBox]
  );

  const handleMouseUp = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false, dragHandle: null }));
  }, []);

  useEffect(() => {
    if (dragState.isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  const handleResetSelection = () => {
    setSelectionBox(
      getDefaultCropRect(
        imageData.width,
        imageData.height,
        defaultCropWidth,
        defaultCropHeight
      )
    );
    onResetSelection?.();
  };

  const handleConfirm = () => {
    onConfirm(clampBox(selectionBox));
  };

  if (!visible) return null;

  return (
    <div className={styles.wrap}>
      <div
        ref={canvasWrapRef}
        className={styles.canvasWrap}
        onMouseDown={handleMouseDown}
        style={{
          cursor: dragState.isDragging
            ? "grabbing"
            : dragState.dragHandle === "move"
              ? "grab"
              : "crosshair",
        }}
      >
        <canvas ref={canvasRef} className={styles.canvas} />
      </div>
      <div className={styles.toolbar}>
        <Button variant="primary" size="sm" onClick={handleConfirm}>
          {t("common.confirm")}
        </Button>
        <Button variant="default" size="sm" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button variant="default" size="sm" onClick={handleResetSelection}>
          {t("keyboard.testCalibration.reset")}
        </Button>
      </div>
    </div>
  );
};

export default FrameCropCanvas;
